<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->
<div class="top-level-container">
  <!-- Situation -->
  @if (situation) {
  <mat-card appearance="outlined" class="dashboard-card situation-card">
    @if (!situation.isCurrent) {
    <div class="situation-label">Previous move</div>
    }
    <h2 class="situation-title">{{ situation.title }}</h2>
    @if (situation.time) {
    <div class="situation-time">{{ situation.time }}</div>
    }
    <div class="situation-description" [innerHTML]="situation.description"></div>
    @if (situation.pastMoves.length > 0) {
    <h3 class="past-moves-heading">Previous Moves</h3>
    @for (pastMove of situation.pastMoves; track pastMove.moveNumber) {
    <div class="past-move">
      <h4 class="situation-title">{{ pastMove.title }}</h4>
      @if (pastMove.time) {
      <div class="situation-time">{{ pastMove.time }}</div>
      }
      <div class="situation-description" [innerHTML]="pastMove.description"></div>
    </div>
    }
    }
  </mat-card>
  }
  <!-- Gallery -->
  @if (unreadArticles && selectedEvaluation.galleryExhibitId) {
  <mat-card appearance="outlined" class="dashboard-card gallery-card">
    @if (+unreadArticles.count > 0) {
    <b>{{ unreadArticles.count }} unread in Gallery</b>
    <a mat-stroked-button href="{{ galleryUrl }}" target="_blank">Open Gallery</a>
    } @else {
    <span class="quiet-text">All Gallery items read</span>
    }
  </mat-card>
  }
  <mat-accordion multi class="dashboard-panels">
    <!-- Actions -->
    <mat-expansion-panel [(expanded)]="showActions">
      <mat-expansion-panel-header>
        <mat-panel-title title="Participant Actions during an Exercise">Actions ({{ actionList.length }})</mat-panel-title>
      </mat-expansion-panel-header>
      @if (myTeamId === activeTeamId) {
      <div class="panel-toolbar">
        @if (!isActionEditMode) {
        <button mat-icon-button (click)="isActionEditMode = true" title="Edit Actions"
          [disabled]="noChanges || !canSubmitTeamScore">
          <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"></mat-icon>
        </button>
        }
        @if (isActionEditMode) {
        <button mat-icon-button (click)="addOrEditAction(null)" title="Add Action">
          <mat-icon class="mdi-24px" fontIcon="mdi-plus-circle"></mat-icon>
        </button>
        <button mat-icon-button (click)="isActionEditMode = false" title="Close Action Editing">
          <mat-icon class="mdi-24px" fontIcon="mdi-close-circle-outline"></mat-icon>
        </button>
        }
      </div>
      }
      @for (action of actionList; track action.id) {
      <div class="panel-row">
        @if (isActionEditMode) {
        <button mat-icon-button (click)="addOrEditAction(action); $event.stopPropagation()" title="Edit Action">
          <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"></mat-icon>
        </button>
        <button mat-icon-button (click)="deleteActionRequest(action); $event.stopPropagation()"
          title="Delete Action">
          <mat-icon class="mdi-24px" fontIcon="mdi-trash-can-outline"></mat-icon>
        </button>
        <span>{{ action.description }}</span>
        } @else {
        <mat-checkbox [checked]="action.isChecked" (change)="checkAction(action.id, $event.checked)"
          [matTooltip]="changedBy(action.id)"
          [disabled]="noChanges || myTeamId !== activeTeamId || !canContributeToTeamScore">
          {{ action.description }}
        </mat-checkbox>
        }
      </div>
      }
    </mat-expansion-panel>
    <!-- Duties -->
    <mat-expansion-panel [(expanded)]="showDuties">
      <mat-expansion-panel-header>
        <mat-panel-title title="Participant Responsibilities during an Exercise">Duties ({{ dutyList.length
          }})</mat-panel-title>
      </mat-expansion-panel-header>
      <div class="panel-toolbar">
        @if (!isDutyEditMode) {
        <button mat-icon-button (click)="isDutyEditMode = true" title="Edit Duties"
          [disabled]="noChanges || !canSubmitTeamScore">
          <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"></mat-icon>
        </button>
        }
        @if (isDutyEditMode) {
        <button mat-icon-button (click)="addOrEditDuty(null)" title="Add Duty">
          <mat-icon class="mdi-24px" fontIcon="mdi-plus-circle"></mat-icon>
        </button>
        <button mat-icon-button (click)="isDutyEditMode = false" title="Close Duty Editing">
          <mat-icon class="mdi-24px" fontIcon="mdi-close-circle-outline"></mat-icon>
        </button>
        }
      </div>
      @for (duty of dutyList; track duty.id) {
      <div class="panel-row">
        @if (isDutyEditMode) {
        <button mat-icon-button (click)="addOrEditDuty(duty); $event.stopPropagation()" title="Edit Duty">
          <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"></mat-icon>
        </button>
        <button mat-icon-button (click)="deleteDutyRequest(duty); $event.stopPropagation()" title="Delete Duty">
          <mat-icon class="mdi-24px" fontIcon="mdi-trash-can-outline"></mat-icon>
        </button>
        }
        <label class="row-label">{{ duty.name }}</label>
        <div class="row-value">
          @if (!isDutyEditMode && canContributeToTeamScore) {
          <mat-select placeholder="Assign Users" [value]="duty.users" multiple (opened)="openDutyUsers(duty)"
            (selectionChange)="updateDutyUsers(duty.id, $event)" (closed)="closeDutyUsers(duty.id)"
            [disabled]="noChanges || myTeamId !== activeTeamId" title="Select a Team Member">
            @for (membership of teamMemberships; track membership.id) {
            <mat-option [value]="getUser(membership.userId)">{{ getUserName(membership.userId) }}
            </mat-option>
            }
          </mat-select>
          }
          @if (isDutyEditMode || !canContributeToTeamScore) {
          <span>{{ getUserNames(duty.users) }}</span>
          }
        </div>
      </div>
      }
    </mat-expansion-panel>
    <!-- Roles -->
    <mat-expansion-panel [(expanded)]="showPermissions">
      <mat-expansion-panel-header>
        <mat-panel-title title="Participant permissions for this Exercise">Roles ({{ teamMemberships.length
          }})</mat-panel-title>
      </mat-expansion-panel-header>
      @for (teamMembership of teamMemberships; track teamMembership.id) {
      <div class="panel-row">
        <label class="row-label">{{ getUserName(teamMembership.userId) }}</label>
        <mat-form-field class="row-value" subscriptSizing="dynamic">
          <mat-label>Role</mat-label>
          <mat-select placeholder="Role" [(ngModel)]="teamMembership.roleId"
            [disabled]="noChanges || !canManageTeam || teamMembership.userId === loggedInUserId">
            @for (role of teamRoles; track role.id) {
            <mat-option [value]="role.id">
              {{ role.name }}
            </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
      }
    </mat-expansion-panel>
  </mat-accordion>
</div>
